import { useState, useEffect, useCallback } from "react";
import { alertService } from "@/services/alert.service";
import type { Alert } from "@/services/alert.service";

interface UseAlertsReturn {
  alerts: Alert[];
  unreadAlerts: Alert[];
  criticalAlerts: Alert[];
  stats: {
    total: number;
    unread: number;
    critical: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
  };
  markAsRead: (alertId: string) => void;
  markAsResolved: (alertId: string) => void;
  executeAction: (alertId: string, actionId: string) => Promise<void>;
  requestNotificationPermission: () => Promise<boolean>;
  clearAll: () => void;
  isLoading: boolean;
}

export const useAlerts = (): UseAlertsReturn => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar alertas iniciais
  useEffect(() => {
    const initialAlerts = alertService.getAlerts();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAlerts(initialAlerts);
    setIsLoading(false);

    alertService.startMonitoring();

    // Configurar listener para mudanças
    const unsubscribe = alertService.addListener((newAlerts) => {
      setAlerts(newAlerts);
    });

    return () => {
      unsubscribe();
      alertService.stopMonitoring();
    };
  }, []);

  // Computar alertas derivados
  const unreadAlerts = alerts.filter(
    (alert) => !alert.isRead && !alert.isResolved,
  );
  const criticalAlerts = alerts.filter(
    (alert) => alert.severity === "critical" && !alert.isResolved,
  );

  // Obter estatísticas
  const stats = alertService.getStats();

  // Ações
  const markAsRead = useCallback((alertId: string) => {
    alertService.markAsRead(alertId);
  }, []);

  const markAsResolved = useCallback((alertId: string) => {
    alertService.markAsResolved(alertId);
  }, []);

  const executeAction = useCallback(
    async (alertId: string, actionId: string) => {
      setIsLoading(true);
      try {
        await alertService.executeAction(alertId, actionId);
      } catch {
        // Error is handled internally
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const requestNotificationPermission = useCallback(async () => {
    return await alertService.requestNotificationPermission();
  }, []);

  const clearAll = useCallback(() => {
    alertService.clearAll();
  }, []);

  return {
    alerts,
    unreadAlerts,
    criticalAlerts,
    stats,
    markAsRead,
    markAsResolved,
    executeAction,
    requestNotificationPermission,
    clearAll,
    isLoading,
  };
};

// Hook para alertas não lidos apenas
export const useUnreadAlerts = () => {
  const { unreadAlerts, stats, markAsRead } = useAlerts();

  return {
    unreadAlerts,
    unreadCount: stats.unread,
    markAsRead,
  };
};

// Hook para alertas críticos apenas
export const useCriticalAlerts = () => {
  const { criticalAlerts, stats, markAsResolved } = useAlerts();

  return {
    criticalAlerts,
    criticalCount: stats.critical,
    markAsResolved,
  };
};
